'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import Header from '@/components/common/Header';
import CategoryHero from './components/CategoryHero';
import SubcategoryFilter from './components/SubcategoryFilter';
import ModelGrid from './components/ModelGrid';

import { catalogService } from '@/services/catalogService';
import { trackCategoryView } from '@/lib/analytics';

// Create context for sharing filter state across components
interface CategoryContextType {
  selectedSubcategory: string;
  setSelectedSubcategory: (subcategory: string) => void;
  allModels: any[];
  filteredModels: any[];
}

const CategoryContext = createContext<CategoryContextType>({
  selectedSubcategory: 'all',
  setSelectedSubcategory: () => {},
  allModels: [],
  filteredModels: [],
});

export const useCategoryContext = () => useContext(CategoryContext);

const CategoryPage = () => {
  const params = useParams();
  const categorySlug = params?.category as string;

  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all');
  const [allModels, setAllModels] = useState<any[]>([]);

  // Calculate filtered models based on selected subcategory
  const filteredModels = selectedSubcategory === 'all' 
    ? allModels 
    : allModels.filter(model => model.subcategory === selectedSubcategory);

  useEffect(() => {
    const loadCategoryData = async () => {
      setLoading(true);
      try {
        const categoryData = await catalogService.getCategoryBySlug(categorySlug);
        if (categoryData) {
          setCategory(categoryData);
          
          // Track category view
          trackCategoryView({
            category: categoryData.name,
          });
          
          const modelsData = await catalogService.getModelsByCategory(categorySlug);
          setAllModels(modelsData || []);
        }
      } catch (error) {
        console.error('Error loading category:', error);
      } finally {
        setLoading(false);
      }
    };

    if (categorySlug) {
      loadCategoryData();
    }
  }, [categorySlug]);

  return (
    <CategoryContext.Provider data-component-id="src/app/catalog/[category]/page.tsx:73:4" data-component-path="src/app/catalog/[category]/page.tsx" data-component-line="73" data-component-end-line="87" data-component-file="page.tsx" data-component-name="CategoryContext.Provider" data-component-content="%7B%22elementName%22%3A%22CategoryContext.Provider%22%7D" 
      value={{ 
        selectedSubcategory, 
        setSelectedSubcategory,
        allModels,
        filteredModels
      }}
    >
      <main data-component-id="src/app/catalog/[category]/page.tsx:81:6" data-component-path="src/app/catalog/[category]/page.tsx" data-component-line="81" data-component-end-line="86" data-component-file="page.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <Header data-component-id="src/app/catalog/[category]/page.tsx:82:8" data-component-path="src/app/catalog/[category]/page.tsx" data-component-line="82" data-component-end-line="82" data-component-file="page.tsx" data-component-name="Header" data-component-content="%7B%22elementName%22%3A%22Header%22%7D" />
        <CategoryHero data-component-id="src/app/catalog/[category]/page.tsx:83:8" data-component-path="src/app/catalog/[category]/page.tsx" data-component-line="83" data-component-end-line="83" data-component-file="page.tsx" data-component-name="CategoryHero" data-component-content="%7B%22elementName%22%3A%22CategoryHero%22%7D" categorySlug={categorySlug} loading={loading} />
        <SubcategoryFilter data-component-id="src/app/catalog/[category]/page.tsx:84:8" data-component-path="src/app/catalog/[category]/page.tsx" data-component-line="84" data-component-end-line="84" data-component-file="page.tsx" data-component-name="SubcategoryFilter" data-component-content="%7B%22elementName%22%3A%22SubcategoryFilter%22%7D" categorySlug={categorySlug} loading={loading} />
        <ModelGrid data-component-id="src/app/catalog/[category]/page.tsx:85:8" data-component-path="src/app/catalog/[category]/page.tsx" data-component-line="85" data-component-end-line="85" data-component-file="page.tsx" data-component-name="ModelGrid" data-component-content="%7B%22elementName%22%3A%22ModelGrid%22%7D" loading={loading} />
      </main>
    </CategoryContext.Provider>
  );
};

export default CategoryPage;